import axios, { type AxiosRequestConfig } from 'axios'
import JSONBig from 'json-bigint'
import { AUTH_SESSION_EXPIRED_EVENT, clearAuthSession, readAuthSession } from '@/utils/authSession'

export interface ApiResponse<T> {
  code: number
  message: string
  data: T
}

export class ApiError extends Error {
  code: number
  status?: number

  constructor(message: string, code = 0, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

const precisionSafeJson = JSONBig({ storeAsString: true })

/** 兼容旧后端把 Snowflake Long 直接输出为 JSON number，避免 JSON.parse 先行舍入。 */
function parseJsonResponse(data: unknown) {
  if (typeof data !== 'string' || !data.trim()) return data
  try {
    return precisionSafeJson.parse(data)
  } catch {
    return data
  }
}

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 15000,
  transformResponse: [parseJsonResponse]
})

http.interceptors.request.use(config => {
  const session = readAuthSession()
  if (session) config.headers.set(session.tokenName, `Bearer ${session.tokenValue}`)
  return config
})

http.interceptors.response.use(
  response => {
    const body = response.data as Partial<ApiResponse<unknown>> | undefined
    if (body && typeof body.code === 'number' && body.code !== 200) {
      return Promise.reject(new ApiError(body.message || '请求失败', body.code, response.status))
    }
    return response
  },
  error => {
    if (!axios.isAxiosError(error)) return Promise.reject(error)

    const body = error.response?.data as Partial<ApiResponse<unknown>> | undefined
    const status = error.response?.status
    const requestUrl = String(error.config?.url || '')

    if (status === 401 && !requestUrl.endsWith('/auth/login')) {
      clearAuthSession()
      window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT))
    }

    if (body && typeof body.code === 'number') {
      return Promise.reject(new ApiError(body.message || '请求失败', body.code, status))
    }

    if (error.code === 'ECONNABORTED') return Promise.reject(new ApiError('请求超时，请稍后重试', 0, status))
    if (!error.response) return Promise.reject(new ApiError('无法连接服务器，请检查后端服务是否已启动'))
    return Promise.reject(new ApiError(`请求失败（HTTP ${status}）`, 0, status))
  }
)

export async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await http.request<ApiResponse<T>>(config)
  return response.data.data
}

export function getErrorMessage(error: unknown, fallback = '操作失败，请稍后重试') {
  return error instanceof Error && error.message ? error.message : fallback
}
